/**
 * TasksKanban — Kanban drag-drop + Ma journée · Important · Planifié · NLP parse.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkles, Plus, Star, Sun, Calendar, User, Loader2, Send, Trash2, Check, MessageSquare, Clock, FileStack, Tag as TagIcon, BarChart3, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import TaskGroupsBar from "@/components/tasks/TaskGroupsBar";
import TaskTemplatesDialog from "@/components/tasks/TaskTemplatesDialog";
import TaskTagsDialog from "@/components/tasks/TaskTagsDialog";
import TaskReportsDialog from "@/components/tasks/TaskReportsDialog";

const COLUMNS = [
  { id: "todo", label: "À faire", color: "bg-slate-100" },
  { id: "in_progress", label: "En cours", color: "bg-blue-100" },
  { id: "done", label: "Fait", color: "bg-emerald-100" },
];
const PRIO_COLOR = { low: "bg-slate-100 text-slate-700", medium: "bg-blue-100 text-blue-700",
                        high: "bg-orange-100 text-orange-700", urgent: "bg-red-100 text-red-700" };
const PRIO_LABEL = { low: "Faible", medium: "Normale", high: "Haute", urgent: "Urgente" };

export default function TasksKanban() {
  const [tasks, setTasks] = useState([]);
  const [view, setView] = useState("all");
  const [stats, setStats] = useState({ by_status: {}, my_day: 0, important: 0, overdue: 0 });
  const [nlpText, setNlpText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [openTask, setOpenTask] = useState(null);
  const [openDetail, setOpenDetail] = useState(false);
  const [comment, setComment] = useState("");
  const [dragId, setDragId] = useState(null);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [groupsRefreshKey, setGroupsRefreshKey] = useState(0);
  const [openTemplates, setOpenTemplates] = useState(false);
  const [openTagsMgr, setOpenTagsMgr] = useState(false);
  const [openReports, setOpenReports] = useState(false);
  const [collapsedCols, setCollapsedCols] = useState({});

  const load = async () => {
    try {
      const params = {};
      if (view !== "all") params.view = view;
      if (activeGroupId) params.group_id = activeGroupId;
      const [t, s] = await Promise.all([
        api.get("/tasks", { params }),
        api.get("/tasks/stats"),
      ]);
      setTasks(t.data.tasks || []);
      setStats(s.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [view, activeGroupId]);

  const toggleColCollapse = (id) => setCollapsedCols(prev => ({ ...prev, [id]: !prev[id] }));

  const nlpParse = async () => {
    if (!nlpText.trim()) return;
    setParsing(true);
    try {
      const p = await api.post("/tasks/parse", { text: nlpText });
      const r = await api.post("/tasks", p.data);
      toast.success(`Tâche IA créée : "${r.data.title}"`);
      setNlpText(""); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setParsing(false); }
  };

  const quickAdd = async () => {
    if (!nlpText.trim()) return;
    try {
      const r = await api.post("/tasks", { title: nlpText, status: "todo", priority: "medium" });
      toast.success(`Tâche ajoutée : "${r.data.title}"`);
      setNlpText(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleImportant = async (id) => { await api.post(`/tasks/${id}/toggle-important`); load(); };
  const toggleMyDay = async (id) => { await api.post(`/tasks/${id}/toggle-my-day`); load(); };
  const completeTask = async (id) => { await api.patch(`/tasks/${id}/complete`); load(); };
  const deleteTask = async (id) => { await api.delete(`/tasks/${id}`); load(); };

  const onDragStart = (id) => setDragId(id);
  const onDrop = async (targetStatus) => {
    if (!dragId) return;
    try {
      await api.patch(`/tasks/${dragId}`, { status: targetStatus });
      setDragId(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openTaskDetail = async (id) => {
    try {
      const r = await api.get(`/tasks/${id}`);
      setOpenTask(r.data); setOpenDetail(true);
    } catch (e) { toast.error(apiError(e)); }
  };

  const addComment = async () => {
    if (!comment.trim() || !openTask) return;
    try {
      await api.post(`/tasks/${openTask.id}/comments`, { content: comment });
      const r = await api.get(`/tasks/${openTask.id}`);
      setOpenTask(r.data); setComment("");
    } catch (e) { toast.error(apiError(e)); }
  };

  const byStatus = COLUMNS.reduce((acc, c) => {
    acc[c.id] = tasks.filter(t => (t.status || "todo") === c.id);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>Tâches</h1>
          <p className="text-sm text-slate-500">Tâches (Kanban) + IA langage naturel · {stats.total} tâches</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className="bg-yellow-100 text-yellow-700"><Sun className="w-3 h-3 mr-1" />{stats.my_day} Ma journée</Badge>
          <Badge className="bg-orange-100 text-orange-700"><Star className="w-3 h-3 mr-1" />{stats.important} Important</Badge>
          {stats.overdue > 0 && <Badge className="bg-red-100 text-red-700"><Clock className="w-3 h-3 mr-1" />{stats.overdue} en retard</Badge>}
          <Button variant="outline" size="sm" onClick={() => setOpenTemplates(true)} data-testid="open-templates-btn"><FileStack className="w-4 h-4 mr-1" />Modèles</Button>
          <Button variant="outline" size="sm" onClick={() => setOpenTagsMgr(true)} data-testid="open-tags-btn"><TagIcon className="w-4 h-4 mr-1" />Tags</Button>
          <Button variant="outline" size="sm" onClick={() => setOpenReports(true)} data-testid="open-reports-btn"><BarChart3 className="w-4 h-4 mr-1" />Rapports</Button>
        </div>
      </div>

      {/* Groups bar (Sprint 87) */}
      <TaskGroupsBar
        key={groupsRefreshKey}
        activeGroupId={activeGroupId}
        onSelectGroup={setActiveGroupId}
        onChanged={() => { setGroupsRefreshKey(k => k + 1); load(); }}
      />

      {/* NLP quick add */}
      <Card className="border-blue-200 bg-blue-50/30" data-testid="nlp-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-600 shrink-0" />
            <Input placeholder='Ex: "Appeler Dupont demain 14h urgent" ou tapez pour ajout rapide'
                     value={nlpText} onChange={e => setNlpText(e.target.value)}
                     onKeyDown={e => { if (e.key === "Enter" && !parsing && nlpText.trim()) { e.preventDefault(); nlpParse(); } }}
                     data-testid="nlp-input" />
            <Button variant="outline" onClick={quickAdd} disabled={!nlpText.trim()} data-testid="quick-add-btn"><Plus className="w-4 h-4" /></Button>
            <Button onClick={nlpParse} disabled={parsing || !nlpText.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="nlp-btn">
              {parsing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}IA
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-2">IA parse : titre + date + priorité + tags · Quick add = titre brut</p>
        </CardContent>
      </Card>

      <Tabs value={view} onValueChange={setView}>
        <TabsList>
          <TabsTrigger value="all">Toutes</TabsTrigger>
          <TabsTrigger value="my_day"><Sun className="w-3 h-3 mr-1" />Ma journée</TabsTrigger>
          <TabsTrigger value="important"><Star className="w-3 h-3 mr-1" />Important</TabsTrigger>
          <TabsTrigger value="planned"><Calendar className="w-3 h-3 mr-1" />Planifié</TabsTrigger>
          <TabsTrigger value="assigned_to_me"><User className="w-3 h-3 mr-1" />Assignées à moi</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Kanban — colonnes collapsibles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {COLUMNS.map(col => (
          <div key={col.id} onDragOver={e => e.preventDefault()} onDrop={() => onDrop(col.id)}
                data-testid={`col-${col.id}`} className="space-y-2 min-h-[120px]">
            <button
              onClick={() => toggleColCollapse(col.id)}
              className={`${col.color} w-full rounded-lg px-3 py-2 flex items-center justify-between sticky top-0 z-10 hover:brightness-95 transition`}
              data-testid={`col-collapse-${col.id}`}
            >
              <span className="font-medium text-sm flex items-center gap-1">
                {collapsedCols[col.id] ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {col.label}
              </span>
              <Badge variant="outline" className="bg-white">{byStatus[col.id]?.length || 0}</Badge>
            </button>
            {!collapsedCols[col.id] && (byStatus[col.id] || []).map((t, i) => (
              <motion.div key={t.id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
                              draggable onDragStart={() => onDragStart(t.id)}
                              data-testid={`task-${t.id}`}>
                <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => openTaskDetail(t.id)}>
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium flex-1 line-clamp-2">{t.title}</p>
                      <div className="flex gap-1 shrink-0">
                        <button onClick={e => { e.stopPropagation(); toggleImportant(t.id); }} data-testid={`star-${t.id}`}>
                          <Star className={`w-4 h-4 ${t.is_important ? "fill-orange-400 text-orange-400" : "text-slate-300"}`} />
                        </button>
                        <button onClick={e => { e.stopPropagation(); toggleMyDay(t.id); }} data-testid={`sun-${t.id}`}>
                          <Sun className={`w-4 h-4 ${t.is_my_day ? "fill-yellow-400 text-yellow-500" : "text-slate-300"}`} />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`${PRIO_COLOR[t.priority] || PRIO_COLOR.medium} text-[10px]`}>{PRIO_LABEL[t.priority] || t.priority}</Badge>
                      {t.due_date && <span className="text-xs text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3" />{t.due_date}</span>}
                      {t.tags?.slice(0, 2).map(tg => <Badge key={tg} variant="outline" className="text-[10px]">#{tg}</Badge>)}
                    </div>
                    <div className="flex justify-end gap-1 pt-1">
                      {t.status !== "done" && <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); completeTask(t.id); }} className="h-6 px-2"><Check className="w-3 h-3" /></Button>}
                      <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); deleteTask(t.id); }} className="h-6 px-2 text-red-500"><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        ))}
      </div>

      {/* Sprint 87 dialogs */}
      <TaskTemplatesDialog open={openTemplates} onOpenChange={setOpenTemplates} onInstantiated={() => { load(); setGroupsRefreshKey(k => k + 1); }} />
      <TaskTagsDialog open={openTagsMgr} onOpenChange={setOpenTagsMgr} onChanged={() => load()} />
      <TaskReportsDialog open={openReports} onOpenChange={setOpenReports} />

      {/* Detail modal */}
      <Dialog open={openDetail} onOpenChange={setOpenDetail}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{openTask?.title}</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          {openTask && (
            <div className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <Badge className={PRIO_COLOR[openTask.priority] || PRIO_COLOR.medium}>{PRIO_LABEL[openTask.priority]}</Badge>
                <Badge>{COLUMNS.find(c => c.id === openTask.status)?.label}</Badge>
                {openTask.due_date && <Badge variant="outline"><Calendar className="w-3 h-3 mr-1" />{openTask.due_date}</Badge>}
                {openTask.tags?.map(tg => <Badge key={tg} variant="outline">#{tg}</Badge>)}
              </div>
              {openTask.description && <p className="text-sm text-slate-700 whitespace-pre-line">{openTask.description}</p>}
              <div>
                <p className="text-sm font-medium mb-2 flex items-center gap-2"><MessageSquare className="w-4 h-4" />Commentaires ({openTask.comments?.length || 0})</p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {openTask.comments?.map(c => (
                    <div key={c.id} className="bg-slate-50 p-2 rounded text-sm">
                      <p className="text-xs text-slate-500">{c.author_name || c.author_email}</p>
                      <p>{c.content}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <Textarea placeholder="Ajouter un commentaire... (mention: @email@domain.fr)"
                                value={comment} onChange={e => setComment(e.target.value)} rows={2} data-testid="comment-input" />
                  <Button onClick={addComment} disabled={!comment.trim()} data-testid="comment-send-btn"><Send className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
